import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 36, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "", innerClassName = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className={`block ${innerClassName}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const ImageReveal = ({ src, alt, className = "", imgClassName = "", delay = 0 }) => (
  <motion.div
    className={`overflow-hidden ${className}`}
    initial={{ clipPath: "inset(8% 8% 8% 8%)", opacity: 0 }}
    whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.2, delay, ease: EASE }}
  >
    <motion.img
      src={src}
      alt={alt}
      loading="lazy"
      className={`h-full w-full object-cover ${imgClassName}`}
      initial={{ scale: 1.12 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.6, delay, ease: EASE }}
    />
  </motion.div>
);

export const Chapter = ({ number, label, dark = false }) => (
  <Reveal>
    <div className="flex items-center gap-4" data-testid={`chapter-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`font-serif italic text-lg ${dark ? "text-terra-light" : "text-terra"}`}>{number}</span>
      <span className={`h-px w-12 ${dark ? "bg-stone-700" : "bg-stone-300"}`} />
      <span className={`font-label text-xs font-semibold uppercase tracking-[0.28em] ${dark ? "text-stone-400" : "text-stone-500"}`}>
        {label}
      </span>
    </div>
  </Reveal>
);

export const ParallaxBand = ({ src, alt, caption, className = "h-[52vh] md:h-[72vh]" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <div ref={ref} data-testid="parallax-band" className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y }}
        className="absolute -top-[10%] left-0 h-[120%] w-full object-cover"
      />
      {caption && (
        <p className="absolute bottom-6 left-6 z-10 max-w-xs bg-ink/55 px-5 py-3 font-label text-[10px] uppercase leading-relaxed tracking-[0.26em] text-ivory backdrop-blur-md md:bottom-10 md:left-12">
          {caption}
        </p>
      )}
    </div>
  );
};
